import * as React from "react";
import styled from "styled-components";

import { MobileNav } from "./mobile/MobileNav";
import { AccountNav } from "./AccountNav/AccountNav";
import { DropdownMenu } from "./DropdownMenu/DropdownMenu";

import { BorderedNavLink } from "./NavLink";
import { BREAK_POINTS } from "../../consts/style";
import { IMenuItem } from "./IMenuItem";
import {
  addTrackingToNavigation,
  getLogoLink,
  locateMenuIndexForPath,
  locateMenuIndexByHeuristic
} from "./navService";
import { Logo } from "../Svg/Logo";
import "url-polyfill"; // side effect - loads URL Polyfill for IE.
import { UNKNOWN_MENU_INDEX } from "./const";

const Nav = styled.nav`
  background: white;
  max-height: 64px;
  position: relative;
  border-bottom: 1px solid #e3e3e3;
  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    max-height: 136px;
    height: 136px;
  }
`;

export const LogoLink = styled(BorderedNavLink)`
  padding: 7px;
  top: 0;
  left: 0;
  width: 64px;
  height: 64px;
  z-index: 1000;
  float: left;
  position: relative;

  & > svg {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 50%;
    left: 50%;
    transform: translateY(-50%) translateX(-50%);
    padding: 8px;
    box-sizing: border-box;
  }

  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    padding: 14px;
    height: 136px;
    width: 101px;
    & > svg {
      padding: 14px;
      height: 100%;
      width: 100%;
    }
  }
`;

const DesktopNav = styled.div`
  display: none;
  width: 100%;
  @media (min-width: ${BREAK_POINTS.MIN.MD}) {
    display: block;
  }
`;

const UpperNavContainer = styled.div`
  border-bottom: 1px solid #f1f1f1;
  height: 48px;
`;

const UpperNavContainer2 = styled(UpperNavContainer)`
  flex-shrink: 0;
  @media (min-width: ${BREAK_POINTS.MIN.LG}) {
    flex-basis: 450px;
  }
`;

const AccountLinks = styled.div``;
const NavContainer = styled.div``;
const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FlexFillLrg = styled(UpperNavContainer)`
  display: none;
  @media (min-width: ${BREAK_POINTS.MIN.LG}) {
    display: block;
    flex-grow: 1;
  }
`;

const FlexFill = styled(UpperNavContainer)`
  display: block;
  flex-grow: 1;
`;

interface Props {
  menuItems: IMenuItem[];
  activeTopMenuIndex: number;
  activeDropdownMenuIndex: number;
}

interface State {
  trackedMenus: any;
  calculatedActiveTopMenuIndex: number;
  calculatedActiveDropdownMenuIndex: number;
}

export class Navigation extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      trackedMenus: addTrackingToNavigation(props.menuItems),
      calculatedActiveTopMenuIndex: props.activeTopMenuIndex,
      calculatedActiveDropdownMenuIndex: props.activeDropdownMenuIndex
    };
  }

  componentDidMount(): void {
    // attempt to locate the active top level menu item and the active sub menu item beneath it.

    let calculatedActiveDropdownMenuIndex = this.state
      .calculatedActiveDropdownMenuIndex;
    let calculatedActiveTopMenuIndex = this.state.calculatedActiveTopMenuIndex;

    // attempt to locate top level index in given menu for the current path
    if (calculatedActiveTopMenuIndex === UNKNOWN_MENU_INDEX) {
      calculatedActiveTopMenuIndex = locateMenuIndexForPath(
        this.props.menuItems,
        window.location.pathname
      );

      // if not found, use some dodgy hard-coded 'hueristics' to try again.
      if (calculatedActiveTopMenuIndex === UNKNOWN_MENU_INDEX) {
        calculatedActiveTopMenuIndex = locateMenuIndexByHeuristic(
          this.props.menuItems,
          window.location.pathname
        );
      }

      // if we found one, fetch index for the sub menu
      if (calculatedActiveTopMenuIndex !== UNKNOWN_MENU_INDEX) {
        calculatedActiveDropdownMenuIndex = locateMenuIndexForPath(
          this.props.menuItems[calculatedActiveTopMenuIndex].children,
          window.location.pathname
        );
      }
    }

    // if no top level menu item, default to first.
    if (calculatedActiveTopMenuIndex === UNKNOWN_MENU_INDEX) {
      calculatedActiveTopMenuIndex = 0;
      calculatedActiveDropdownMenuIndex = UNKNOWN_MENU_INDEX;
    }

    this.setState({
      calculatedActiveTopMenuIndex: calculatedActiveTopMenuIndex,
      calculatedActiveDropdownMenuIndex: calculatedActiveDropdownMenuIndex
    });
  }

  render() {
    const logoLinkHref = getLogoLink(
      this.state.trackedMenus,
      this.state.calculatedActiveTopMenuIndex
    );
    return (
      <Nav>
        <DesktopNav data-comments="DesktopNav">
          <LogoLink
            componentType="navDesktopLogoLink"
            data-id="desktop-logolink"
            href={logoLinkHref}
          >
            <Logo id="desktop-nav" />
          </LogoLink>
          <FlexContainer>
            <FlexFillLrg />
            <NavContainer>
              <UpperNavContainer>
                {this.state.trackedMenus.map((topMenu, index) => (
                  <BorderedNavLink
                    data-id={topMenu.tracking}
                    componentType="navDesktopTopLink"
                    key={index}
                    href={topMenu.link}
                    className={
                      index === this.state.calculatedActiveTopMenuIndex
                        ? "active"
                        : ""
                    }
                  >
                    <span>{topMenu.title}</span>
                  </BorderedNavLink>
                ))}
              </UpperNavContainer>
              <DropdownMenu
                menuItems={
                  this.state.trackedMenus[
                    this.state.calculatedActiveTopMenuIndex ==
                    UNKNOWN_MENU_INDEX
                      ? 0
                      : this.state.calculatedActiveTopMenuIndex
                  ].children || []
                }
                activeMenuIndex={this.state.calculatedActiveDropdownMenuIndex}
              />
            </NavContainer>
            <FlexFill />
            <UpperNavContainer2 data-comment="desktop-nav-upper-2">
              <AccountLinks>
                <AccountNav
                  text="See our plans"
                  href="/electricity-gas/plans.html"
                />
              </AccountLinks>
            </UpperNavContainer2>
          </FlexContainer>
        </DesktopNav>
        <MobileNav
          logoLinkHref={logoLinkHref}
          menuItems={this.state.trackedMenus}
        />
      </Nav>
    );
  }
}
